import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Avatar } from "@heroui/react";
import { Button, Card, EmptyState, PageLoader } from "../../../../components/shared";
import { cn } from "../../../../lib/cn";
import { getStatusLabel } from "../../../../constants/statuses";
import { useTeamDetail, useTeamMembers } from "../../../../lib/react-query/teams";
import { useProjectsList } from "../../../../lib/react-query/projects";
import TeamWorkspaceHeader from "../../../../components/Teams/TeamWorkspaceHeader";
import TeamMembersSection from "../../../../components/Teams/TeamMembersSection";
import TeamInvitesSection from "../../../../components/Teams/TeamInvitesSection";
import TeamStorageSection from "../../../../components/Teams/TeamStorageSection";
import TeamSettingsSection from "../../../../components/Teams/TeamSettingsSection";
import MoveProjectModal from "../../../../components/Projects/MoveProjectModal";
import ProjectFiles from "../../ProjectFiles";

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${parseFloat((bytes / 1024 ** i).toFixed(1))} ${units[i]}`;
};

const StatBox = ({ label, value, borderColor = "border-l-primary/40" }) => (
  <div className={cn("border rounded-lg p-3 border-l-2 bg-white", borderColor)}>
    <p className="text-xs text-grey">{label}</p>
    <p className="text-xl font-semibold text-darkgrey">{value}</p>
  </div>
);

const formatProjectDate = (value) => {
  if (!value) return "Not provided";

  const rawValue = String(value);
  const date = /^\d{2}\/\d{2}\/\d{4}$/.test(rawValue)
    ? (() => {
        const [month, day, year] = rawValue.split("/").map(Number);
        return new Date(year, month - 1, day);
      })()
    : new Date(`${rawValue}T00:00:00`);

  if (Number.isNaN(date.getTime())) return "Not provided";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getActiveParticipants = (project) => {
  const participants = [
    project.owner,
    ...(project.members || []).filter((member) => member.status === "ACTIVE"),
  ].filter(Boolean);

  return Array.from(
    new Map(participants.map((participant) => [participant.email || participant.id, participant])).values()
  );
};

const getTeamRoleLabel = (role) => {
  if (!role) return "Member";
  return role.charAt(0) + role.slice(1).toLowerCase();
};

const TeamWorkspacePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [moveProject, setMoveProject] = useState(null);
  const teamId = Number(id);

  const currentUser = useSelector((state) => state?.user?.user);
  const currentUserId = currentUser?.id;

  const { data: team, isLoading: teamLoading, isError: teamError } = useTeamDetail(teamId);
  const { data: members = [] } = useTeamMembers(teamId);
  const {
    data: projectsData,
    isLoading: projectsLoading,
    isError: projectsError,
  } = useProjectsList({ team_id: teamId, include_all: true }, !!teamId);
  const teamProjects = projectsData?.items || [];
  const activeProjectId = Number(searchParams.get("projectId")) || null;
  const activeProject = teamProjects.find((project) => project.id === activeProjectId) || null;
  const activeSection = useMemo(() => {
    if (location.pathname.endsWith("/members")) return "members";
    if (location.pathname.endsWith("/invites")) return "invites";
    if (location.pathname.endsWith("/storage")) return "storage";
    if (location.pathname.endsWith("/settings")) return "settings";
    return "overview";
  }, [location.pathname]);

  useEffect(() => {
    if (activeSection === "invites" && team?.is_personal) {
      navigate(`/teams/${teamId}`, { replace: true });
    }
  }, [activeSection, team, teamId, navigate]);

  const currentMembership = Array.isArray(members)
    ? members.find((m) => m?.user?.id === currentUserId)
    : null;
  const canManageTeam =
    team?.owner === currentUserId ||
    currentMembership?.role === "OWNER" ||
    currentMembership?.role === "ADMIN";

  if (teamLoading) return <PageLoader />;
  if (teamError || !team) {
    return (
      <div className="py-8">
        <div className="rounded-xl border border-surface-border bg-white p-6 shadow-sm max-w-lg mx-auto">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-danger/10 flex items-center justify-center">
              <svg className="w-5 h-5 text-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.007M5.25 19.5h13.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H5.25A1.5 1.5 0 003.75 6v12a1.5 1.5 0 001.5 1.5z" />
              </svg>
            </div>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-primary">Team unavailable</h1>
              <p className="mt-1 text-sm text-mediumgrey">
                This team may have been removed, or you don&apos;t have access to it.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/teams")}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
                >
                  Back to teams
                </button>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="rounded-lg border border-surface-border bg-white px-4 py-2 text-sm font-semibold text-darkgrey transition-colors hover:bg-surface-hover"
                >
                  Retry
                </button>
              </div>
          </div>
          </div>
        </div>
      </div>
    );
  }

  const storageUsed = team?.storage_used_bytes || 0;
  const storageQuota = Math.max(team?.storage_quota_bytes || 1, 1);
  const storagePercent = Math.min(Math.round((storageUsed / storageQuota) * 100), 100);

  return (
    <div className="space-y-6 pb-6">
      <TeamWorkspaceHeader
        team={team}
        canManageTeam={canManageTeam}
        teamId={teamId}
        activeSection={activeSection}
        projects={teamProjects}
      />

      {activeSection === "overview" && (
        <>
          <Card className="p-4">
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-center">
                <p className="text-lg font-semibold text-primary">Team Overview</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <StatBox label="Members" value={team.member_count ?? 0} borderColor="border-l-secondary/60" />
                <StatBox label="Projects" value={team.project_count ?? 0} borderColor="border-l-primary/40" />
                <StatBox label="Files Used" value={`${storagePercent}%`} borderColor="border-l-warning/60" />
                <StatBox label="Type" value={team.is_personal ? "Personal workspace" : "Shared team"} borderColor="border-l-success/60" />
              </div>
              <div className="w-full bg-lightgrey rounded-full h-1">
                <div
                  className={cn(
                    "h-1 rounded-full transition-all duration-500",
                    storagePercent < 60 ? "bg-secondary" : storagePercent < 80 ? "bg-warning" : "bg-danger"
                  )}
                  style={{ width: `${storagePercent}%` }}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="border rounded-lg p-3">
                  <p className="text-xs text-grey">Description</p>
                  <p className="text-sm whitespace-normal break-words [overflow-wrap:anywhere]">
                    {team.description || (team.is_personal ? "Your default workspace for private work." : "—")}
                  </p>
                </div>
                <div className="border rounded-lg p-3">
                  <p className="text-xs text-grey">Files used</p>
                  <p className="text-sm">
                    {formatBytes(storageUsed)} of {formatBytes(storageQuota)}
                  </p>
                </div>
              </div>
              {team.is_personal && (
                <div className="rounded-lg border border-surface-border bg-surface/60 p-3 text-sm text-grey">
                  Personal workspaces are created automatically for each account. Use them for private work and create a shared team when you need collaboration, invites, or shared ownership workflows.
                </div>
              )}
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-primary">Projects in this team</h2>
              {teamProjects.length > 0 && (
                <Link
                  to={`/projects?team_id=${teamId}`}
                  className="text-sm font-medium text-primary hover:text-secondary-dark transition-colors"
                >
                  View all →
                </Link>
              )}
            </div>
            {projectsLoading ? (
              <div className="space-y-2" aria-label="Loading projects">
                {[1, 2].map((row) => (
                  <div key={row} className="h-14 animate-pulse rounded-lg bg-surface-hover" />
                ))}
              </div>
            ) : projectsError ? (
              <p className="rounded-lg border border-surface-border bg-surface px-4 py-3 text-sm text-grey">
                Project details are temporarily unavailable.
              </p>
            ) : teamProjects.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-surface-border bg-white">
                <div className="hidden grid-cols-[minmax(0,1.3fr)_0.6fr_1fr_1.4fr_auto] gap-4 border-b border-surface-border px-4 py-2 text-xs font-medium text-grey md:grid">
                  <span>Project</span>
                  <span>Status</span>
                  <span>Date range</span>
                  <span>Participation</span>
                  <span className="sr-only">Actions</span>
                </div>
                {teamProjects.map((project) => {
                  const participants = getActiveParticipants(project);
                  const participantNames = participants
                    .map((participant) => participant.name)
                    .filter(Boolean)
                    .join(", ");
                  const openProject = () => navigate(`/projects/${project.id}/tasks`);
                  const handleProjectRowKeyDown = (event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    openProject();
                  };

                  return (
                    <div
                      key={project.id}
                      role="group"
                      aria-label={`Open project ${project.title}`}
                      // Composite row owns a keyboard shortcut while child actions remain independent.
                      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                      tabIndex={0}
                      onClick={openProject}
                      onKeyDown={handleProjectRowKeyDown}
                      className="grid gap-3 rounded-sm border-b border-surface-border px-4 py-3 outline-none last:border-b-0 focus-visible:ring-2 focus-visible:ring-secondary md:grid-cols-[minmax(0,1.3fr)_0.6fr_1fr_1.4fr_auto] md:items-center"
                    >
                      <Link
                        to={`/projects/${project.id}/tasks`}
                        onClick={(event) => event.stopPropagation()}
                        className="truncate rounded-sm font-medium text-primary outline-none hover:text-secondary-dark focus-visible:ring-2 focus-visible:ring-secondary"
                      >
                        {project.title}
                      </Link>
                      <span className="text-sm text-darkgrey">{getStatusLabel(project.status)}</span>
                      <span className="text-sm text-darkgrey">
                        {formatProjectDate(project.start_date)} - {formatProjectDate(project.end_date)}
                      </span>
                      <div className="min-w-0">
                        <div className="mb-1 flex -space-x-2" aria-hidden="true">
                          {participants.slice(0, 4).map((participant) => (
                            <Avatar
                              key={participant.email || participant.id}
                              name={participant.name || participant.email}
                              src={participant.profile_image_url || undefined}
                              className="h-7 w-7 border-2 border-white text-[10px]"
                            />
                          ))}
                        </div>
                        <p className="truncate text-xs text-grey">
                          {participantNames || "No participants"}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {project.owner?.id === currentUserId && (
                          <Button
                            variant="transparent"
                            color="primary"
                            size="sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              setMoveProject(project);
                            }}
                          >
                            Move
                          </Button>
                        )}
                        <Link
                          to={`/projects/${project.id}/files`}
                          aria-label={`Files for ${project.title}`}
                          onClick={(event) => event.stopPropagation()}
                          className="rounded-sm text-sm font-medium text-primary outline-none hover:text-secondary-dark focus-visible:ring-2 focus-visible:ring-secondary"
                        >
                          Files
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                compact
                title="No projects in this team yet."
                description="Create a project to start sharing files and tasks."
                className="border-dashed bg-surface/50"
              />
            )}
          </Card>

          <Card className="p-5">
            <h2 className="mb-3 text-base font-semibold text-primary">Team members</h2>
            <div className="overflow-hidden rounded-lg border border-surface-border bg-white">
              <div className="hidden grid-cols-[minmax(0,1fr)_0.7fr_0.5fr] gap-4 border-b border-surface-border px-4 py-2 text-xs font-medium text-grey sm:grid">
                <span>Name</span>
                <span>Role</span>
                <span>Project count</span>
              </div>
              {members.map((member) => {
                const projectCount = member.project_count || 0;
                return (
                  <div
                    key={member.id}
                    className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 border-b border-surface-border px-4 py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_0.7fr_0.5fr] sm:gap-4"
                  >
                    <span className="truncate text-sm font-medium text-primary">
                      {member.user.full_name || member.user.email}
                    </span>
                    <span className="text-sm text-darkgrey">{getTeamRoleLabel(member.role)}</span>
                    <span className="col-span-2 text-sm text-grey sm:col-span-1">
                      {projectCount} {projectCount === 1 ? "project" : "projects"}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      )}

      {activeSection === "members" && (
        <TeamMembersSection
          teamId={teamId}
          team={team}
          members={members}
          canManageTeam={canManageTeam}
          currentUserId={currentUserId}
          projects={teamProjects}
        />
      )}

      {activeSection === "invites" && (
        <TeamInvitesSection teamId={teamId} canManageTeam={canManageTeam} />
      )}

      {activeSection === "storage" && (
        activeProject ? (
          <ProjectFiles
            projectId={activeProject.id}
            projectTitle={activeProject.title}
            parentLabel="Team Files"
            parentPath={`/teams/${teamId}/storage`}
            backLabel="Back to team files"
            backPath={`/teams/${teamId}/storage`}
          />
        ) : (
          <TeamStorageSection
            teamId={teamId}
            activeProjectId={activeProjectId}
            onOpenProjectFiles={(projectId) => {
              const next = new URLSearchParams(searchParams);
              next.set("projectId", String(projectId));
              next.delete("folder");
              setSearchParams(next);
            }}
          />
        )
      )}

      {activeSection === "settings" && (
        <TeamSettingsSection
          teamId={teamId}
          team={team}
          members={members}
          canManageTeam={canManageTeam}
          currentUserId={currentUserId}
        />
      )}

      <MoveProjectModal
        isOpen={!!moveProject}
        onClose={() => setMoveProject(null)}
        project={moveProject}
        currentTeamId={teamId}
      />
    </div>
  );
};

export default TeamWorkspacePage;
